import React, { useState, useMemo, useEffect } from 'react';
import { Table, Tag, Button, Card, Space, Typography, Drawer, Descriptions, Select, Input, message, Tooltip, Badge, Statistic, Row, Col, Form, Modal, Grid, List } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { feedbackApi, usersApi } from '../../../api/client';
import dayjs from 'dayjs';
import { 
    BugOutlined, 
    BulbOutlined, 
    MessageOutlined, 
    UserOutlined, 
    LinkOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    SyncOutlined,
    CloseCircleOutlined,
    SearchOutlined,
    DeleteOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const statusConfig: Record<string, { color: string; label: string; icon: React.ReactNode }> = {
    OPEN: { color: 'blue', label: 'Otwarte', icon: <ClockCircleOutlined /> },
    IN_PROGRESS: { color: 'orange', label: 'W realizacji', icon: <SyncOutlined spin /> },
    RESOLVED: { color: 'green', label: 'Naprawione', icon: <CheckCircleOutlined /> },
    REJECTED: { color: 'error', label: 'Odrzucone', icon: <CloseCircleOutlined /> },
    CLOSED: { color: 'default', label: 'Zamknięte', icon: <CheckCircleOutlined /> },
};

const priorityConfig: Record<string, { color: string; label: string }> = {
    LOW: { color: 'default', label: 'Niski' },
    MEDIUM: { color: 'blue', label: 'Średni' },
    HIGH: { color: 'orange', label: 'Wysoki' },
    CRITICAL: { color: 'red', label: 'Krytyczny' },
};

export const FeedbackTab: React.FC = () => {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const feedbackIdParam = searchParams.get('id');
    const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [priorityFilter, setPriorityFilter] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<string | null>(null);

    const { data: feedbackList, isLoading } = useQuery({
        queryKey: ['feedback-admin', feedbackIdParam], // Include ID to ensure fresh fetch if navigating directly
        queryFn: () => feedbackApi.getAll().then(res => res.data),
    });

    // Handle deep linking to a specific feedback
    useEffect(() => {
        if (feedbackIdParam && feedbackList) {
            // Check if we already have this feedback selected OR if the ID in URL is different from currently selected
            if (!selectedFeedback || selectedFeedback.id !== feedbackIdParam) {
                const feedback = feedbackList.find((f: any) => f.id === feedbackIdParam);
                if (feedback) {
                    setSelectedFeedback(feedback);
                    setIsDrawerOpen(true);
                }
            }
        }
    }, [feedbackIdParam, feedbackList, selectedFeedback]);

    const { data: users } = useQuery({
        queryKey: ['users'],
        queryFn: () => usersApi.getAll().then(res => res.data),
    });

    const [form] = Form.useForm();

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => feedbackApi.update(id, data),
        onSuccess: () => {
            message.success('Zgłoszenie zaktualizowane');
            queryClient.invalidateQueries({ queryKey: ['feedback-admin'] });
            setIsDrawerOpen(false);
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd podczas aktualizacji');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => feedbackApi.delete(id),
        onSuccess: () => {
            message.success('Zgłoszenie usunięte');
            queryClient.invalidateQueries({ queryKey: ['feedback-admin'] });
            setIsDrawerOpen(false);
            setSelectedFeedback(null);
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd podczas usuwania');
        }
    });

    // Update form values when selected feedback changes
    React.useEffect(() => {
        if (selectedFeedback) {
            form.setFieldsValue({
                status: selectedFeedback.status,
                priority: selectedFeedback.priority,
                assignedToId: selectedFeedback.assignedToId,
                adminComment: selectedFeedback.adminComment,
            });
        }
    }, [selectedFeedback, form]);

    const filteredData = useMemo(() => {
        if (!feedbackList) return [];
        return feedbackList.filter((item: any) => {
            const matchesSearch = !searchText || 
                item.title?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.reporter?.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.reporter?.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
                item.reporter?.email?.toLowerCase().includes(searchText.toLowerCase());
            const matchesStatus = !statusFilter || item.status === statusFilter;
            const matchesPriority = !priorityFilter || item.priority === priorityFilter;
            const matchesType = !typeFilter || item.type === typeFilter;
            return matchesSearch && matchesStatus && matchesPriority && matchesType;
        });
    }, [feedbackList, searchText, statusFilter, priorityFilter, typeFilter]);

    const stats = useMemo(() => {
        if (!feedbackList) return { total: 0, open: 0, inProgress: 0, resolved: 0, critical: 0 };
        return {
            total: feedbackList.length,
            open: feedbackList.filter((f: any) => f.status === 'OPEN').length,
            inProgress: feedbackList.filter((f: any) => f.status === 'IN_PROGRESS').length,
            resolved: feedbackList.filter((f: any) => f.status === 'RESOLVED' || f.status === 'CLOSED').length,
            critical: feedbackList.filter((f: any) => f.priority === 'CRITICAL' && f.status !== 'RESOLVED' && f.status !== 'CLOSED').length,
        };
    }, [feedbackList]);

    const columns = [
        {
            title: 'Typ',
            dataIndex: 'type',
            key: 'type',
            width: 100,
            filters: [
                { text: 'Błąd', value: 'BUG' },
                { text: 'Pomysł', value: 'FEATURE_REQUEST' },
                { text: 'Inne', value: 'OTHER' },
            ],
            onFilter: (value: any, record: any) => record.type === value,
            render: (type: string) => (
                <Space>
                    {type === 'BUG' ? <BugOutlined style={{ color: '#ff4d4f' }} /> : <BulbOutlined style={{ color: '#faad14' }} />}
                    {type === 'BUG' ? 'Błąd' : type === 'FEATURE_REQUEST' ? 'Pomysł' : 'Inne'}
                </Space>
            )
        },
        {
            title: 'Tytuł',
            dataIndex: 'title',
            key: 'title',
            render: (text: string, record: any) => (
                <div>
                    <div style={{ fontWeight: 600, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                        od: {record.reporter ? `${record.reporter.firstName} ${record.reporter.lastName}` : 'Anonim'} • {dayjs(record.createdAt).format('DD.MM.YYYY HH:mm')}
                    </div>
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            filters: Object.entries(statusConfig).map(([val, cfg]) => ({ text: cfg.label, value: val })),
            onFilter: (value: any, record: any) => record.status === value,
            sorter: (a: any, b: any) => a.status.localeCompare(b.status),
            render: (status: string) => {
                const config = statusConfig[status] || statusConfig.OPEN;
                return <Tag color={config.color} icon={config.icon}>{config.label}</Tag>;
            }
        },
        {
            title: 'Priorytet',
            dataIndex: 'priority',
            key: 'priority',
            width: 110,
            filters: Object.entries(priorityConfig).map(([val, cfg]) => ({ text: cfg.label, value: val })),
            onFilter: (value: any, record: any) => record.priority === value,
            sorter: (a: any, b: any) => {
                const order = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
                return (order[a.priority] || 3) - (order[b.priority] || 3);
            },
            render: (priority: string) => {
                const config = priorityConfig[priority] || priorityConfig.MEDIUM;
                return <Tag color={config.color}>{config.label}</Tag>;
            }
        },
        {
            title: 'Przypisano',
            dataIndex: 'assignedTo',
            key: 'assignedTo',
            width: 120,
            render: (assignedTo: any) => assignedTo 
                ? <Text>{assignedTo.firstName} {assignedTo.lastName}</Text>
                : <Text type="secondary">—</Text>
        },
        {
            title: 'Akcje',
            key: 'actions',
            width: 100,
            render: (_: any, record: any) => (
                <Button type="link" onClick={() => {
                    setSelectedFeedback(record);
                    setIsDrawerOpen(true);
                }}>
                    Szczegóły
                </Button>
            )
        }
    ];

    const handleUpdate = (values: any) => {
        if (selectedFeedback) {
            updateMutation.mutate({
                id: selectedFeedback.id,
                data: values
            });
        }
    };

    return (
        <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic title="Wszystkie zgłoszenia" value={stats.total} prefix={<MessageOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic title="Otwarte" value={stats.open} valueStyle={{ color: '#1677ff' }} prefix={<ClockCircleOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic title="W realizacji" value={stats.inProgress} valueStyle={{ color: '#faad14' }} prefix={<SyncOutlined spin />} />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card size="small">
                        <Statistic 
                            title={isMobile ? "Krytyczne" : "Krytyczne (nie zamknięte)"} 
                            value={stats.critical} 
                            valueStyle={{ color: stats.critical > 0 ? '#ff4d4f' : '#52c41a' }} 
                            prefix={<BugOutlined />} 
                        />
                    </Card>
                </Col>
            </Row>

            <Card 
                title="Zgłoszenia błędów i pomysły na rozwój" 
                extra={!isMobile && (
                    <Space>
                        <Input
                            placeholder="Szukaj..."
                            prefix={<SearchOutlined />}
                            style={{ width: 200 }}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                        />
                        <Select
                            placeholder="Status"
                            style={{ width: 130 }}
                            allowClear
                            onChange={(val) => setStatusFilter(val)}
                            options={Object.entries(statusConfig).map(([val, cfg]) => ({ value: val, label: cfg.label }))}
                        />
                        <Select
                            placeholder="Priorytet"
                            style={{ width: 120 }}
                            allowClear
                            onChange={(val) => setPriorityFilter(val)}
                            options={Object.entries(priorityConfig).map(([val, cfg]) => ({ value: val, label: cfg.label }))}
                        />
                        <Select
                            placeholder="Typ"
                            style={{ width: 110 }}
                            allowClear
                            onChange={(val) => setTypeFilter(val)}
                            options={[
                                { value: 'BUG', label: 'Błędy' },
                                { value: 'FEATURE_REQUEST', label: 'Pomysły' },
                                { value: 'OTHER', label: 'Inne' },
                            ]}
                        />
                    </Space>
                )}
            >
                {isMobile && (
                    <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Input
                            placeholder="Szukaj zgłoszenia..."
                            prefix={<SearchOutlined />}
                            onChange={(e) => setSearchText(e.target.value)}
                            allowClear
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Select
                                placeholder="Status"
                                style={{ flex: 1 }}
                                allowClear
                                onChange={(val) => setStatusFilter(val)}
                                options={Object.entries(statusConfig).map(([val, cfg]) => ({ value: val, label: cfg.label }))}
                            />
                            <Select
                                placeholder="Priorytet"
                                style={{ flex: 1 }}
                                allowClear
                                onChange={(val) => setPriorityFilter(val)}
                                options={Object.entries(priorityConfig).map(([val, cfg]) => ({ value: val, label: cfg.label }))}
                            />
                        </div>
                    </div>
                )}
                {isMobile ? (
                    <List
                        dataSource={filteredData}
                        loading={isLoading}
                        pagination={{ pageSize: 10, size: 'small', showTotal: (total) => `${total} zgłoszeń` }}
                        renderItem={(record: any) => (
                            <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                <Card
                                    size="small"
                                    hoverable
                                    onClick={() => {
                                        setSelectedFeedback(record);
                                        setIsDrawerOpen(true);
                                    }}
                                    style={{ width: '100%', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                                    styles={{ body: { padding: '12px' } }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1677ff', paddingRight: 8 }}>{record.title}</div>
                                        <Tag color={statusConfig[record.status]?.color || 'default'} style={{ margin: 0, whiteSpace: 'nowrap' }}>
                                            {statusConfig[record.status]?.label || record.status}
                                        </Tag>
                                    </div>
                                    <div style={{ color: '#888', fontSize: 11, marginBottom: 8 }}>
                                        {record.reporter ? `${record.reporter.firstName} ${record.reporter.lastName}` : 'Anonim'} • {dayjs(record.createdAt).format('DD.MM.YYYY HH:mm')}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Space size={4}>
                                            {record.type === 'BUG' ? <BugOutlined style={{ color: '#ff4d4f' }} /> : <BulbOutlined style={{ color: '#faad14' }} />}
                                            <span style={{ fontSize: 11, color: '#555' }}>{record.type === 'BUG' ? 'Błąd' : record.type === 'FEATURE_REQUEST' ? 'Pomysł' : 'Inne'}</span>
                                        </Space>
                                        <Tag color={priorityConfig[record.priority]?.color || 'default'} style={{ margin: 0, fontSize: 10 }}>
                                            {priorityConfig[record.priority]?.label || record.priority}
                                        </Tag>
                                    </div>
                                </Card>
                            </List.Item>
                        )}
                    />
                ) : (
                    <Table
                        dataSource={filteredData}
                        columns={columns}
                        loading={isLoading}
                        rowKey="id"
                        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total: number) => `Łącznie: ${total}` }}
                        size="middle"
                    />
                )}
            </Card>

            <Drawer
                title="Zarządzaj zgłoszeniem"
                width={650}
                open={isDrawerOpen}
                onClose={() => { 
                    setIsDrawerOpen(false); 
                    setSelectedFeedback(null); 
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('id');
                    setSearchParams(newParams);
                }}
                extra={
                    <Space>
                        <Button 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                if (selectedFeedback) {
                                    Modal.confirm({
                                        title: 'Czy na pewno chcesz usunąć to zgłoszenie?',
                                        content: 'Tej operacji nie można cofnąć.',
                                        okText: 'Tak, usuń',
                                        okType: 'danger',
                                        cancelText: 'Anuluj',
                                        onOk: () => deleteMutation.mutate(selectedFeedback.id),
                                    });
                                }
                            }}
                            loading={deleteMutation.isPending}
                        >
                            Usuń
                        </Button>
                        <Button onClick={() => { setIsDrawerOpen(false); setSelectedFeedback(null); }}>Zamknij</Button>
                        <Button 
                            type="primary" 
                            onClick={() => form.submit()}
                            loading={updateMutation.isPending}
                        >
                            Zapisz zmiany
                        </Button>
                    </Space>
                }
            >
                {selectedFeedback && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Descriptions title="Informacje o zgłoszeniu" bordered column={1} size="small">
                            <Descriptions.Item label="Zgłaszający">
                                <Space>
                                    <UserOutlined />
                                    {selectedFeedback.reporter 
                                        ? `${selectedFeedback.reporter.firstName} ${selectedFeedback.reporter.lastName} (${selectedFeedback.reporter.email})`
                                        : 'Anonimowy użytkownik'
                                    }
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Data zgłoszenia">
                                {dayjs(selectedFeedback.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ostatnia aktualizacja">
                                {dayjs(selectedFeedback.updatedAt).format('DD.MM.YYYY HH:mm:ss')}
                            </Descriptions.Item>
                            <Descriptions.Item label="Strona">
                                <Tooltip title={selectedFeedback.pageUrl}>
                                    <a href={selectedFeedback.pageUrl} target="_blank" rel="noreferrer">
                                        <LinkOutlined /> {selectedFeedback.pageUrl?.substring(0, 50)}...
                                    </a>
                                </Tooltip>
                            </Descriptions.Item>
                            <Descriptions.Item label="System">
                                <div style={{ fontSize: 11, color: '#666' }}>
                                    {selectedFeedback.metadata?.browser} | {selectedFeedback.metadata?.os} | {selectedFeedback.metadata?.screenSize}
                                </div>
                            </Descriptions.Item>
                        </Descriptions>

                        <Card size="small" title="Treść zgłoszenia" headStyle={{ background: '#fafafa' }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <Text type="secondary">Tytuł:</Text>
                                    <Title level={5} style={{ margin: '4px 0 8px 0' }}>{selectedFeedback.title}</Title>
                                </div>
                                <div>
                                    <Text type="secondary">Opis:</Text>
                                    <Paragraph style={{ marginTop: 4 }}>{selectedFeedback.description}</Paragraph>
                                </div>
                                {selectedFeedback.stepsToReproduce && (
                                    <div>
                                        <Text type="secondary">Kroki do odtworzenia:</Text>
                                        <Paragraph style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{selectedFeedback.stepsToReproduce}</Paragraph>
                                    </div>
                                )}
                                {selectedFeedback.expectedBehavior && (
                                    <div>
                                        <Text type="secondary">Oczekiwane zachowanie:</Text>
                                        <Paragraph style={{ marginTop: 4 }}>{selectedFeedback.expectedBehavior}</Paragraph>
                                    </div>
                                )}
                                {selectedFeedback.actualBehavior && (
                                    <div>
                                        <Text type="secondary">Faktyczne zachowanie:</Text>
                                        <Paragraph style={{ marginTop: 4 }}>{selectedFeedback.actualBehavior}</Paragraph>
                                    </div>
                                )}
                            </Space>
                        </Card>

                        <Card size="small" title="Zarządzanie zgłoszeniem" headStyle={{ background: '#fafafa' }}>
                            <Form 
                                form={form}
                                layout="vertical"
                                onFinish={handleUpdate}
                            >
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <Form.Item name="status" label={<Text strong>Status</Text>}>
                                            <Select 
                                                style={{ width: '100%' }} 
                                                options={Object.entries(statusConfig).map(([val, cfg]) => ({ value: val, label: cfg.label }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name="priority" label={<Text strong>Priorytet</Text>}>
                                            <Select 
                                                style={{ width: '100%' }} 
                                                options={Object.entries(priorityConfig).map(([val, cfg]) => ({ value: val, label: cfg.label }))}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item name="assignedToId" label={<Text strong>Przypisz do</Text>}>
                                    <Select 
                                        style={{ width: '100%' }} 
                                        allowClear
                                        placeholder="Wybierz osobę"
                                        options={users?.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                                    />
                                </Form.Item>
                                <Form.Item name="adminComment" label={<Text strong>Komentarz administratora (widoczny dla użytkownika)</Text>}>
                                    <Input.TextArea 
                                        rows={3} 
                                        placeholder="Napisz odpowiedź do użytkownika..." 
                                    />
                                </Form.Item>
                            </Form>
                        </Card>
                    </Space>
                )}
            </Drawer>
        </div>
    );
};
